import type {
  ColDef,
  FilterChangedEvent,
  GridOptions,
  GridReadyEvent,
  ModelUpdatedEvent,
  RowClickedEvent,
} from 'ag-grid-community'
// License is set globally in web/src/main.tsx
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react'
import { useMemo, useState } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { TABLE_LABELS } from '@/components/lemmala-design-system/table/labels'

import { DATE_FILTER_PRESETS } from './consts'
import { SearchContextProvider } from './contexts/search-provider'
import type { RowClickMeta } from './types'
import {
  agGridDateFilterComparator,
  createFillerColumn,
  extractColumnValue,
  normalizeAgGridDateValue,
} from './utils/column-utils'
import { datePresetStart } from './utils/date-range-filter'
import { getDefaultColDef, mergeGridOptions } from './utils/grid-theme'
import { tablePlaceholderVariants } from './variants'

import './sort-icons.css'

export interface AgTableProps<TRowData = Record<string, unknown>> {
  rowData: TRowData[]
  columnDefs: ColDef<TRowData>[]
  gridOptions?: GridOptions<TRowData>
  defaultColDef?: ColDef<TRowData>
  onGridReady?: (event: GridReadyEvent<TRowData>) => void
  onRowClick?: (row: TRowData, meta: RowClickMeta) => void
  className?: string
  loading?: boolean
  emptyStateContent?: React.ReactNode
  searchContext?: {
    query: string
    results: unknown[]
  }
  withFillerColumn?: boolean
  domLayout?: AgGridReactProps['domLayout']
  onFilterChanged?: (params: FilterChangedEvent<TRowData>) => void
  borderless?: boolean
}

/**
 * Core AG Grid table component with standardized theming and configuration.
 *
 * This component provides a consistent foundation for all AG Grid tables in the application,
 * with proper TypeScript generics, theme integration, and performance optimizations.
 *
 * Features:
 * - Automatic theme application using semantic design tokens
 * - Performance-optimized defaults
 * - Loading states and empty state handling
 * - Search context support for highlighting
 * - Type-safe props with generics
 *
 * @example
 * ```tsx
 * <AgTable<CompanyRow>
 *   rowData={companies}
 *   columnDefs={columnDefinitions}
 *   onGridReady={(api) => setGridApi(api)}
 *   onRowClick={(row) => navigate(`/company/${row.id}`)}
 *   loading={isLoading}
 * />
 * ```
 */

export const AgTable = <TRowData extends Record<string, unknown> = Record<string, unknown>>({
  rowData,
  columnDefs,
  gridOptions,
  defaultColDef,
  onGridReady,
  onRowClick,
  className,
  loading = false,
  emptyStateContent,
  searchContext,
  withFillerColumn = false,
  domLayout,
  onFilterChanged,
  borderless = false,
}: AgTableProps<TRowData>) => {
  // No dynamic last-column resizing logic; CSS handles the right-edge handle visibility

  // Merge user options with our defaults
  const mergedGridOptions = useMemo(() => mergeGridOptions<TRowData>(gridOptions), [gridOptions])

  const mergedDefaultColDef = useMemo(
    () => ({
      ...getDefaultColDef<TRowData>(),
      ...defaultColDef,
    }),
    [defaultColDef],
  )

  const finalColumnDefs = useMemo(() => {
    // clone to avoid mutating caller's columnDefs
    const defs = columnDefs.map((columnDef) => {
      const clonedColumnDef = { ...columnDef }

      if (clonedColumnDef.filter === 'agDateColumnFilter') {
        const existingFilterParams =
          typeof clonedColumnDef.filterParams === 'object' && clonedColumnDef.filterParams
            ? clonedColumnDef.filterParams
            : {}

        clonedColumnDef.filterValueGetter =
          columnDef.filterValueGetter ??
          ((params) => {
            if (!params.data) {
              return null
            }

            return normalizeAgGridDateValue(extractColumnValue(columnDef, params.data))
          })
        clonedColumnDef.filterParams = {
          ...existingFilterParams,
          filterOptions: [
            'inRange',
            ...DATE_FILTER_PRESETS.map(({ value, label }) => ({
              displayKey: value,
              displayName: label,
              numberOfInputs: 0 as const,
              predicate: (_: unknown[], cellValue: Date | null) =>
                !!cellValue && cellValue >= datePresetStart(value),
            })),
          ],
          comparator:
            'comparator' in existingFilterParams &&
            typeof existingFilterParams.comparator === 'function'
              ? existingFilterParams.comparator
              : agGridDateFilterComparator,
        }
      }

      return clonedColumnDef
    })

    if (withFillerColumn) {
      return [...defs, createFillerColumn<TRowData>()]
    }
    return defs
  }, [columnDefs, withFillerColumn])

  // Handle grid ready event
  const handleGridReady = (event: GridReadyEvent<TRowData>) => {
    setShowAutoHeightEmptyState(
      domLayout === 'autoHeight' && event.api.getDisplayedRowCount() === 0,
    )
    // Ensure any onGridReady provided via grid options still runs
    mergedGridOptions.onGridReady?.(event)
    // Then call the consumer-provided handler
    onGridReady?.(event)
  }

  // Handle row click events
  const handleRowClicked = (event: RowClickedEvent<TRowData>) => {
    if (!onRowClick || !event.data) return

    const nativeEvent = event.event as MouseEvent | undefined

    if (nativeEvent) {
      const target = nativeEvent.target as HTMLElement
      const isInteractiveElement = target.closest(
        'button, [role="button"], [data-interactive="true"], a, input, select, textarea',
      )

      if (isInteractiveElement) {
        return
      }
    }

    // Cmd (macOS) / Ctrl (Windows/Linux) click signals intent to open in a new tab.
    const openInNewTab = !!nativeEvent && (nativeEvent.metaKey || nativeEvent.ctrlKey)

    onRowClick(event.data, { openInNewTab })
  }

  const handleFilterChanged = (event: FilterChangedEvent<TRowData>) => {
    mergedGridOptions.onFilterChanged?.(event)
    onFilterChanged?.(event)
  }

  const [showAutoHeightEmptyState, setShowAutoHeightEmptyState] = useState(
    domLayout === 'autoHeight' && (!rowData || rowData.length === 0),
  )

  const handleModelUpdated = (event: ModelUpdatedEvent<TRowData>) => {
    setShowAutoHeightEmptyState(
      domLayout === 'autoHeight' && event.api.getDisplayedRowCount() === 0,
    )
    mergedGridOptions.onModelUpdated?.(event)
  }

  // Show loading state
  if (loading) {
    return (
      <div className={cn(tablePlaceholderVariants({ borderless }), className)}>
        <div className='absolute inset-0 flex items-center justify-center'>
          <div className='text-main-foreground-secondary'>{TABLE_LABELS.loading}</div>
        </div>
      </div>
    )
  }

  // Always render without SearchContextProvider for print layout to avoid issues
  const shouldUseSearchContext = searchContext && domLayout !== 'print'

  // Use empty array if no data to ensure grid initializes (needed for gridApi)
  // But hide the grid visually when empty to preserve original UI
  const effectiveRowData = rowData && rowData.length > 0 ? rowData : []
  const isEmpty = !rowData || rowData.length === 0

  const useNativeOverlay = mergedGridOptions.noRowsOverlayComponent !== undefined

  if (isEmpty && !useNativeOverlay) {
    return (
      <div
        className={cn(tablePlaceholderVariants({ borderless }), className, {
          'min-h-36': domLayout === 'autoHeight',
        })}
      >
        {/* Hidden grid to ensure initialization and gridApi availability */}
        <div className='pointer-events-none absolute inset-0 opacity-0'>
          {shouldUseSearchContext ? (
            <SearchContextProvider value={searchContext}>
              <AgGridReact<TRowData>
                domLayout={domLayout}
                rowData={effectiveRowData}
                columnDefs={finalColumnDefs}
                defaultColDef={mergedDefaultColDef}
                {...mergedGridOptions}
                onGridReady={handleGridReady}
                onRowClicked={handleRowClicked}
                onFilterChanged={handleFilterChanged}
              />
            </SearchContextProvider>
          ) : (
            <AgGridReact<TRowData>
              domLayout={domLayout}
              rowData={effectiveRowData}
              columnDefs={finalColumnDefs}
              defaultColDef={mergedDefaultColDef}
              {...mergedGridOptions}
              onGridReady={handleGridReady}
              onRowClicked={handleRowClicked}
              onFilterChanged={handleFilterChanged}
            />
          )}
        </div>
        {/* Original empty state UI */}
        <div className='absolute inset-0 flex items-center justify-center'>
          {emptyStateContent || (
            <div className='text-center'>
              <div className='text-main-foreground-secondary'>
                {TABLE_LABELS.noResultsToDisplay}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        // AG Grid's absolutely positioned aria live region otherwise anchors to the page and grows it.
        'ag-theme-quartz relative',
        borderless && 'ag-table-borderless',
        {
          'shadow-sm': domLayout !== 'print',
          'ag-auto-height-empty-state': showAutoHeightEmptyState,
        },
        className,
      )}
    >
      {shouldUseSearchContext ? (
        <SearchContextProvider value={searchContext}>
          <AgGridReact<TRowData>
            domLayout={domLayout}
            rowData={effectiveRowData}
            columnDefs={finalColumnDefs}
            defaultColDef={mergedDefaultColDef}
            {...mergedGridOptions}
            onGridReady={handleGridReady}
            onRowClicked={handleRowClicked}
            onFilterChanged={handleFilterChanged}
            onModelUpdated={handleModelUpdated}
          />
        </SearchContextProvider>
      ) : (
        <AgGridReact<TRowData>
          domLayout={domLayout}
          rowData={effectiveRowData}
          columnDefs={finalColumnDefs}
          defaultColDef={mergedDefaultColDef}
          {...mergedGridOptions}
          onGridReady={handleGridReady}
          onRowClicked={handleRowClicked}
          onFilterChanged={handleFilterChanged}
          onModelUpdated={handleModelUpdated}
        />
      )}
    </div>
  )
}

AgTable.displayName = 'AgTable'
